import type { ReactNode } from "react";

export const TOAST_TIMING = {
	fade: 200,
	minimum: 500,
	maximum: 4000,
	gap: 100,
} as const;

export type ToastId = number;
export type ToastType = "info" | "success" | "warning" | "error";
type ToastPhase = "idle" | "entering" | "visible" | "exiting" | "gap";

interface Notification {
	id: ToastId;
	message: ReactNode;
	type: ToastType;
}

interface ToastSnapshot {
	notification: Notification | null;
	phase: ToastPhase;
}

export class ToastQueue {
	private nextId = 0;
	private pending: Notification[] = [];
	private listeners = new Set<() => void>();
	private snapshot: ToastSnapshot = { notification: null, phase: "idle" };
	private timer: ReturnType<typeof setTimeout> | undefined;
	private visibleSince = 0;

	getSnapshot = () => this.snapshot;

	subscribe = (listener: () => void) => {
		this.listeners.add(listener);
		if (this.listeners.size === 1 && this.snapshot.phase === "idle") {
			this.showNext();
		}
		return () => {
			this.listeners.delete(listener);
			if (this.listeners.size > 0) return;
			clearTimeout(this.timer);
			// Preserve waiting notifications if the viewport remounts (StrictMode,
			// error recovery), without leaving a timer running off screen.
			if (this.snapshot.notification && this.snapshot.phase !== "exiting") {
				this.pending.unshift(this.snapshot.notification);
			}
			this.publish(null, "idle");
		};
	};

	enqueue(message: ReactNode, type: ToastType = "info"): ToastId {
		const notification = { id: ++this.nextId, message, type };
		this.pending.push(notification);
		if (this.snapshot.phase === "idle") this.showNext();
		else this.scheduleExit();
		return notification.id;
	}

	update(id: ToastId, { render }: { render: ReactNode }) {
		const current = this.snapshot.notification;
		if (current?.id === id) {
			if (this.snapshot.phase !== "exiting" && current.message !== render) {
				this.publish({ ...current, message: render }, this.snapshot.phase);
			}
			return;
		}
		this.pending = this.pending.map((item) =>
			item.id === id ? { ...item, message: render } : item,
		);
	}

	cancelPending(id: ToastId) {
		this.pending = this.pending.filter((item) => item.id !== id);
		this.scheduleExit();
	}

	private publish(notification: Notification | null, phase: ToastPhase) {
		this.snapshot = { notification, phase };
		for (const listener of this.listeners) listener();
	}

	private schedule(callback: () => void, delay: number) {
		clearTimeout(this.timer);
		this.timer = setTimeout(callback, delay);
	}

	private showNext() {
		if (this.listeners.size === 0) return;
		const next = this.pending.shift();
		if (!next) {
			this.publish(null, "idle");
			return;
		}
		this.publish(next, "entering");
		this.schedule(() => {
			this.visibleSince = Date.now();
			this.publish(this.snapshot.notification, "visible");
			this.scheduleExit();
		}, TOAST_TIMING.fade);
	}

	private scheduleExit() {
		if (this.snapshot.phase !== "visible") return;
		// Display time excludes both fades. Progress updates never extend it.
		const duration =
			this.pending.length > 0 ? TOAST_TIMING.minimum : TOAST_TIMING.maximum;
		const remaining = this.visibleSince + duration - Date.now();
		if (remaining <= 0) this.exit();
		else this.schedule(() => this.exit(), remaining);
	}

	private exit() {
		this.publish(this.snapshot.notification, "exiting");
		this.schedule(() => {
			this.publish(null, "gap");
			this.schedule(() => this.showNext(), TOAST_TIMING.gap);
		}, TOAST_TIMING.fade);
	}
}

export const toastQueue = new ToastQueue();

export const toast = {
	info: (message: ReactNode) => toastQueue.enqueue(message, "info"),
	success: (message: ReactNode) => toastQueue.enqueue(message, "success"),
	warn: (message: ReactNode) => toastQueue.enqueue(message, "warning"),
	error: (message: ReactNode) => toastQueue.enqueue(message, "error"),
	loading: (message: ReactNode) => toastQueue.enqueue(message, "info"),
	update: (id: ToastId, options: { render: ReactNode }) =>
		toastQueue.update(id, options),
	cancelPending: (id: ToastId) => toastQueue.cancelPending(id),
};
